import { DOCUMENT, inject, Injectable } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { PRIMARY_OUTLET, Router, UrlSegmentGroup } from '@angular/router';

/**
 * The app scrolls inside `main > .scrollable` (see `MainLayout`), not the document,
 * so Angular's default window-based scroller reads and writes the wrong element and
 * `withInMemoryScrolling()` has nothing to restore.
 *
 * Delegates to whichever element the active layout registered, falling back to the
 * window for layouts that have no inner scroller (auth, legal).
 */
@Injectable({ providedIn: 'root' })
export class AppViewportScroller extends ViewportScroller {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);

  private container: HTMLElement | null = null;
  private lastPath = '';

  /** Called by a layout with its own scrolling element; pass `null` when it is torn down. */
  setContainer(container: HTMLElement | null): void {
    this.container = container;
  }

  getScrollPosition(): [number, number] {
    const el = this.container;
    return el ? [el.scrollLeft, el.scrollTop] : [window.scrollX, window.scrollY];
  }

  scrollToPosition([left, top]: [number, number], options?: ScrollOptions): void {
    // Opening a dialog in the `modal` outlet is a navigation too, but the page behind it
    // is the same one the user was reading — scrolling it would yank it out from under
    // the overlay. Same for a query-param change (a filter, a tab).
    const path = this.primaryPath();
    const samePage = path === this.lastPath;
    this.lastPath = path;

    if (samePage) return;

    (this.container ?? window).scrollTo({ left, top, ...options });
  }

  setHistoryScrollRestoration(scrollRestoration: 'auto' | 'manual'): void {
    window.history.scrollRestoration = scrollRestoration;
  }

  // `anchorScrolling` is disabled, so the router never calls either of the two methods
  // below. They exist to satisfy the abstract class — flesh them out if it is turned on.

  setOffset(): void {}

  scrollToAnchor(anchor: string, options?: ScrollOptions): void {
    // `scrollIntoView` already targets the nearest scrollable ancestor, container or window.
    this.document.getElementById(anchor)?.scrollIntoView(options);
  }

  /** The primary outlet's path alone — no query string, no secondary outlets. */
  private primaryPath(): string {
    const segments: string[] = [];
    let group: UrlSegmentGroup | undefined = this.router.parseUrl(this.router.url).root;

    while (group) {
      segments.push(...group.segments.map(({ path }) => path));
      group = group.children[PRIMARY_OUTLET];
    }

    return segments.join('/');
  }
}
